import { RegistryAttributeType } from '@/api/backend/types';
import { formatDateTime, formatIsoDate } from '@/lib/utils';
import { ListTable, ListTableCell, ListTableHeader, ListTableRow } from '@opaz/ui-components';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type RegistryAttributesListTableProps = {
  title: string;
  attributes: RegistryAttributeType[];
};

export const RegistryAttributesListTable = ({ title, attributes }: RegistryAttributesListTableProps) => {
  const { t, i18n } = useTranslation();

  const attributeValue = useCallback(
    (attribute: RegistryAttributeType) => {
      switch (attribute.valueType) {
        case 'STRING':
          return attribute.value;
        case 'DATE':
          return formatIsoDate(attribute.value, i18n.language);
        case 'REF_DATA': {
          // Handle comma-separated values
          const values = attribute.value.split(',').map((val) => val.trim());
          const translatedValues = values.map((val) =>
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            t(('backend.codes.' + val) as any, val),
          );
          return translatedValues.join('\n');
        }
        case 'TRANSLATED_STRING': {
          const obj = JSON.parse(attribute.value);
          return obj[i18n.language as keyof typeof obj] || obj[Object.keys(obj)[0]];
        }
        case 'INTEGER':
          return attribute.value;
        case 'NUMERIC':
          return attribute.value;
        case 'BOOLEAN':
          return attribute.value === 'true' ? t('common.yes') : t('common.no');
        case 'DATE_TIME':
          return formatDateTime(attribute.value, i18n.language);
        case 'TIMESTAMP':
          return formatDateTime(attribute.value, i18n.language);
        default:
          return attribute.value;
      }
    },
    [i18n.language, t],
  );

  if (attributes?.length === 0) {
    return null;
  }

  return (
    <>
      <h2 className="text-foreground text-2xl font-bold mb-4">{title}</h2>

      <ListTable className="mb-12">
        {attributes?.map((attribute, index) => (
          <ListTableRow key={index}>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <ListTableHeader>{t(('backend.codes.' + attribute.code) as any)}</ListTableHeader>
            <ListTableCell className="whitespace-pre-line">{attributeValue(attribute) ?? '-'}</ListTableCell>
          </ListTableRow>
        ))}
      </ListTable>
    </>
  );
};
